import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Coleccion, DatosUniverso, ElementoDe } from '../modelo/tipos';
import type { FuenteDatos } from './fuente';

interface ValorDatos {
  datos: DatosUniverso;
  fuente: string;
  /** Añade un elemento al principio de una colección. */
  agregar: <K extends Coleccion>(coleccion: K, elemento: ElementoDe<K>) => void;
  /** Aplica cambios parciales a un elemento por su id. */
  actualizar: <K extends Coleccion>(coleccion: K, id: string, cambios: Partial<ElementoDe<K>>) => void;
}

const Contexto = createContext<ValorDatos | null>(null);

export function DatosProvider({ fuente, children }: { fuente: FuenteDatos; children: ReactNode }) {
  const [datos, setDatos] = useState<DatosUniverso | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    fuente
      .cargar()
      .then((d) => { if (vivo) setDatos(d); })
      .catch(() => { if (vivo) setError(true); });
    return () => { vivo = false; };
  }, [fuente]);

  const agregar = useCallback(<K extends Coleccion>(coleccion: K, elemento: ElementoDe<K>) => {
    setDatos((d) => (d ? { ...d, [coleccion]: [elemento, ...(d[coleccion] as ElementoDe<K>[])] } : d));
  }, []);

  const actualizar = useCallback(<K extends Coleccion>(coleccion: K, id: string, cambios: Partial<ElementoDe<K>>) => {
    setDatos((d) => {
      if (!d) return d;
      const lista = d[coleccion] as ElementoDe<K>[];
      return { ...d, [coleccion]: lista.map((x) => (x.id === id ? { ...x, ...cambios } : x)) };
    });
  }, []);

  const valor = useMemo(
    () => (datos ? { datos, fuente: fuente.nombre, agregar, actualizar } : null),
    [datos, fuente.nombre, agregar, actualizar]
  );

  if (error) return <p className="muted" style={{ padding: 24 }}>No se han podido cargar los datos.</p>;
  if (!valor) return <p className="muted breathe" style={{ padding: 24 }}>Preparando tu espacio…</p>;
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useDatos(): ValorDatos {
  const v = useContext(Contexto);
  if (!v) throw new Error('useDatos debe usarse dentro de DatosProvider');
  return v;
}
